import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { CacheEntryInfo } from "../services/cache-types";
import { formatBytes, formatDateTime } from "./cache-utils";

interface CacheTagAccordionProps {
  title: string;
  keys: string[];
  entriesByKey: Map<string, CacheEntryInfo>;
  disabled: boolean;
  onPurgeKey: (key: string) => void;
  /** Omitted for the "Untagged" group, which is not a real tag. */
  onPurgeTag?: () => void;
}

const CacheTagAccordion = ({
  title,
  keys,
  entriesByKey,
  disabled,
  onPurgeKey,
  onPurgeTag,
}: CacheTagAccordionProps) => (
  <Accordion
    disableGutters
    slotProps={{ transition: { unmountOnExit: true } }}
    sx={{
      mb: 1.5,
      "&:before": { display: "none" },
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      borderRadius: "8px !important",
      overflow: "hidden",
    }}
  >
    <AccordionSummary
      // Rendered as a div (not the default <button>) so the purge-tag
      // IconButton inside it is valid DOM nesting.
      component="div"
      expandIcon={<ExpandMoreIcon />}
      sx={{ bgcolor: "grey.50", "&:hover": { bgcolor: "grey.100" } }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}
      >
        <LabelOutlinedIcon color="primary" />
        <Typography sx={{ fontWeight: 600, wordBreak: "break-all" }}>
          {title}
        </Typography>
        <Chip
          label={`${keys.length} key${keys.length !== 1 ? "s" : ""}`}
          size="small"
          color="primary"
          sx={{ ml: "auto", fontWeight: 600 }}
        />
        {onPurgeTag && (
          <Tooltip title="Purge every key under this tag">
            <span>
              <IconButton
                size="small"
                color="error"
                aria-label={`purge tag ${title}`}
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  onPurgeTag();
                }}
                onFocus={(event) => event.stopPropagation()}
              >
                <DeleteSweepIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ p: 0 }}>
      <List dense disablePadding>
        {keys.map((key) => {
          const entry = entriesByKey.get(key);
          return (
            <ListItem
              key={key}
              divider
              secondaryAction={
                <Tooltip title="Purge this key">
                  <span>
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label={`purge key ${key}`}
                      disabled={disabled}
                      onClick={() => onPurgeKey(key)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              }
            >
              <ListItemText
                primary={key}
                secondary={
                  entry
                    ? `${formatBytes(entry.sizeBytes)} · Created ${formatDateTime(entry.createdUtc)} · Last accessed ${formatDateTime(entry.lastAccessedUtc)}`
                    : undefined
                }
                slotProps={{
                  primary: {
                    sx: { fontFamily: "monospace", wordBreak: "break-all" },
                  },
                }}
                sx={{ pl: 2, pr: 4 }}
              />
            </ListItem>
          );
        })}
      </List>
    </AccordionDetails>
  </Accordion>
);

export default CacheTagAccordion;
